/**
 * Round 113 — Brand Brain Setup Page.
 *
 * Renders after the user finishes Business Brain and BEFORE Dashboard
 * on first login. Two paths:
 *   1. "I already have branding" — upload logo (+ optional
 *      guidelines and website URL) → Gemini vision extracts
 *      palette / personality / style; user reviews and saves.
 *   2. "Build my brand" — 6 quick questions → Gemini generates a
 *      full brand kit; user reviews and saves.
 *
 * On save (either path), we POST /brand-brain/onboarding/complete
 * and navigate to /dashboard. The dashboard's Brand Brain gate
 * checks `brand_brain.onboarding_completed` so the user is only
 * redirected here on first arrival.
 *
 * Also reachable from Settings → "Brand Brain" for later edits.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Palette, Upload, MagicWand, Globe, Sparkle,
  CheckCircle, ArrowRight, ArrowLeft, X, FloppyDisk,
  Star, Trash, PlusCircle, ArrowsHorizontal,
} from '@phosphor-icons/react';
import api, { brandBrainAPI } from '@/utils/api';

const BrandBrainSetupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const embedded = searchParams.get('embedded') === '1';

  const [mode, setMode] = useState(null); // null | 'upload' | 'build' | 'review'
  const [brandBrain, setBrandBrain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Upload-path state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [guidelinesFile, setGuidelinesFile] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');

  // Build-path state
  const [answers, setAnswers] = useState({
    business_name: '',
    niche: '',
    audience: '',
    vibe: '',
    color_prefs: '',
    competitors: '',
  });

  // Round 218A P0 (2026-02-07) — Brand Brain consumes Business Brain.
  // Prefill business_name / niche / audience from the customer's
  // saved Business Brain so we never ask questions Business Brain
  // already knows. If the customer changes them here, their edits
  // still win — Business Brain updates on save.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apiModule = (await import('@/utils/api')).default;
        const resp = await apiModule.get('/business-brain/plan');
        const plan = resp?.data?.plan || {};
        if (cancelled) return;
        setAnswers((prev) => ({
          ...prev,
          business_name: prev.business_name || plan.business_name || '',
          niche:         prev.niche         || plan.niche || plan.business_type || '',
          audience:      prev.audience      || plan.target_audience || plan.audience || '',
        }));
      } catch (_) {
        // Silent — the customer can still fill in fields manually.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Phase 8 (Group A, 2026-08-02): gate Brand Brain behind Business Brain
  // completion. Branding should never come before the business has been
  // defined. If the customer arrives here with no Business Brain plan
  // saved yet, we bounce them to /business-brain with a friendly toast.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const api = (await import('@/utils/api')).default;
        const resp = await api.get('/business-brain/plan');
        const plan = resp?.data?.plan || null;
        const hasCore = !!(
          plan && (plan.niche || plan.main_offer || plan.target_audience ||
                   plan.business_idea || plan.business_name)
        );
        if (!hasCore && !cancelled) {
          toast.info('Set up Business Brain first — branding is based on your business.');
          navigate('/business-brain?next=/brand-brain-setup', { replace: true });
        }
      } catch (err) {
        // If the check fails, don't block — let the page render normally.
        console.info('[BrandBrainSetup] Business Brain gate check skipped:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Load current brand brain on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await brandBrainAPI.get();
        if (!cancelled) setBrandBrain(data?.brand_brain || null);
      } catch (err) {
        console.warn('brand-brain get failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const goDashboard = () => {
    if (embedded) return;
    navigate('/dashboard');
  };

  // Round 218A P0 FINAL (2026-02-07) — Brand Brain approval routes
  // via the canonical next-action resolver. When foundation becomes
  // complete on Brand save, this lands on Sales Page automatically.
  // No dashboard detour, no manual navigation decision.
  const goToNextAction = async () => {
    if (embedded) return;
    try {
      const res = await api.get('/foundation/next-action');
      const route = res?.data?.action?.next_route || '/dashboard';
      navigate(route);
    } catch (_) {
      navigate('/dashboard');
    }
  };

  const finishOnboarding = async (successMsg = 'Brand Brain saved.') => {
    try {
      await brandBrainAPI.completeOnboarding();
      toast.success(successMsg);
      await goToNextAction();
    } catch (err) {
      console.error(err);
      toast.error('Saved brand fields but failed to mark onboarding complete.');
    }
  };

  // ── UPLOAD PATH ─────────────────────────────────────────────────
  const handleLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setLogoPreview('');
    }
  };

  const runUploadAnalysis = async () => {
    if (!logoFile && !websiteUrl) {
      toast.error('Upload a logo or provide a website URL.');
      return;
    }
    setBusy(true);
    try {
      // 1) analyze-logo (also uploads the file into brand_brain.logo_urls via upload endpoint separately for durable storage)
      let latest = brandBrain;
      if (logoFile) {
        // Store the logo file in object storage (durable) — separate from analysis
        try {
          const up = await brandBrainAPI.uploadLogo(logoFile, 'primary');
          latest = up?.brand_brain || latest;
        } catch (e) {
          console.warn('logo upload failed, continuing with analysis only', e);
        }
        // Analyze the logo — merges extracted brand kit into brand_brain
        try {
          const a = await brandBrainAPI.analyzeLogo(logoFile, true);
          latest = a?.brand_brain || latest;
        } catch (e) {
          console.warn('logo analysis failed', e);
          toast.error('Logo analysis failed. You can still fill fields manually.');
        }
      }
      // 2) optional guidelines
      if (guidelinesFile) {
        try {
          const g = await brandBrainAPI.uploadGuidelines(guidelinesFile);
          latest = g?.brand_brain || latest;
        } catch (e) {
          console.warn('guidelines upload failed', e);
        }
      }
      // 3) optional website analysis
      if (websiteUrl && websiteUrl.trim()) {
        try {
          const w = await brandBrainAPI.analyzeWebsite(websiteUrl.trim(), true);
          latest = w?.brand_brain || latest;
        } catch (e) {
          console.warn('website analysis failed', e);
        }
      }
      setBrandBrain(latest);
      setMode('review');
    } finally {
      setBusy(false);
    }
  };

  // ── BUILD PATH ──────────────────────────────────────────────────
  const runBuildGeneration = async () => {
    if (!answers.business_name && !answers.niche) {
      toast.error('Tell us the business name or niche at minimum.');
      return;
    }
    setBusy(true);
    try {
      const r = await brandBrainAPI.generateFromQuestionnaire(answers, true);
      setBrandBrain(r?.brand_brain || brandBrain);
      setMode('review');
    } catch (err) {
      console.error(err);
      toast.error('Brand generation failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // ── REVIEW / SAVE ───────────────────────────────────────────────
  const handleFieldChange = (path, value) => {
    setBrandBrain((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const keys = path.split('.');
      let cursor = next;
      for (let i = 0; i < keys.length - 1; i += 1) {
        cursor[keys[i]] = { ...(cursor[keys[i]] || {}) };
        cursor = cursor[keys[i]];
      }
      cursor[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const saveEditsAndAdvance = async () => {
    if (!brandBrain) return;
    setBusy(true);
    try {
      // Only send editable fields (strip system keys).
      const fields = {
        business_name: brandBrain.business_name,
        tagline: brandBrain.tagline,
        primary_colors: brandBrain.primary_colors,
        secondary_colors: brandBrain.secondary_colors,
        fonts: brandBrain.fonts,
        brand_voice: brandBrain.brand_voice,
        brand_personality: brandBrain.brand_personality,
        brand_style: brandBrain.brand_style,
        icon_style: brandBrain.icon_style,
        illustration_style: brandBrain.illustration_style,
        photography_style: brandBrain.photography_style,
        website_url: brandBrain.website_url,
      };
      const r = await brandBrainAPI.patch(fields);
      setBrandBrain(r?.brand_brain || brandBrain);
      // Advance to Logo Studio (Phase C). If the user came from the
      // upload path with logos already, we still let them generate.
      setMode('logos');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save brand fields.');
    } finally {
      setBusy(false);
    }
  };

  // Legacy alias so the review card's saveEditsAndFinish label
  // still works — but the flow now advances to the logo studio.
  const saveEditsAndFinish = saveEditsAndAdvance;

  const skipForNow = async () => {
    // Marks onboarding complete without saving new fields so the
    // dashboard gate doesn't loop.  User can revisit from Settings.
    await finishOnboarding('You can add branding later from Settings.');
  };

  // ── Render ──────────────────────────────────────────────────────
  const containerClass = embedded
    ? 'w-full'
    : 'min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-start justify-center py-10 px-4';

  return (
    <div className={containerClass} data-testid="brand-brain-setup-page">
      <div className={embedded ? 'w-full' : 'w-full max-w-3xl'}>
        {!embedded && (
          <header className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-1 text-xs uppercase tracking-widest text-emerald-300">
              <Palette size={14} /> Brand Brain
            </div>
            <h1 className="mt-4 text-3xl sm:text-4xl font-semibold text-white">
              Set your brand once. Use it everywhere.
            </h1>
            <p className="mt-3 text-slate-400 text-base">
              Every builder inside PayGBot — sales pages, emails, videos, ebooks, ads — will
              apply the branding you save here automatically. You will never be asked to
              upload your logo or colors twice.
            </p>
          </header>
        )}

        {loading && (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 text-slate-400 text-sm">
            Loading Brand Brain…
          </div>
        )}

        {/* Path chooser */}
        {!loading && !mode && (
          <div className="grid gap-4 sm:grid-cols-2">
            <PathCard
              testid="brand-brain-choose-upload"
              icon={Upload}
              title="I already have branding"
              description="Upload your logo, optional brand guidelines, and website — we'll extract colors, personality and style with vision AI."
              onClick={() => setMode('upload')}
            />
            <PathCard
              testid="brand-brain-choose-build"
              icon={MagicWand}
              title="Build my brand"
              description="Answer a few quick questions and we'll generate a complete brand kit — palette, typography, personality and tagline."
              onClick={() => setMode('build')}
            />

            <div className="sm:col-span-2 mt-2 flex items-center justify-between">
              <button
                data-testid="brand-brain-skip"
                onClick={skipForNow}
                className="text-sm text-slate-500 hover:text-slate-300 transition"
              >
                Skip for now — I'll do this later
              </button>
              {brandBrain?.onboarding_completed && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                  <CheckCircle size={14} weight="fill" /> Brand Brain already set — you can edit any field
                </span>
              )}
            </div>
          </div>
        )}

        {/* UPLOAD PATH */}
        {!loading && mode === 'upload' && (
          <section
            data-testid="brand-brain-upload-form"
            className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8 space-y-6"
          >
            <BackButton onClick={() => setMode(null)} />

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Primary logo <span className="text-slate-500">(PNG, SVG, JPG, PDF)</span>
              </label>
              <input
                type="file"
                accept=".png,.svg,.jpg,.jpeg,.webp,.pdf"
                onChange={handleLogoPick}
                data-testid="brand-brain-logo-input"
                className="block w-full text-sm text-slate-300 file:mr-4 file:rounded-lg file:border-0 file:bg-emerald-500 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-emerald-400"
              />
              {logoPreview && (
                <div className="mt-3 rounded-xl border border-slate-700 bg-slate-800/60 p-4 inline-block">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="max-h-32 max-w-xs object-contain"
                    data-testid="brand-brain-logo-preview"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Brand guidelines <span className="text-slate-500">(optional PDF)</span>
              </label>
              <input
                type="file"
                accept=".pdf,.docx"
                onChange={(e) => setGuidelinesFile(e.target.files?.[0] || null)}
                data-testid="brand-brain-guidelines-input"
                className="block w-full text-sm text-slate-300 file:mr-4 file:rounded-lg file:border-0 file:bg-slate-700 file:px-4 file:py-2 file:text-sm file:text-slate-200 hover:file:bg-slate-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Website URL <span className="text-slate-500">(optional — we'll analyze for palette + tone)</span>
              </label>
              <div className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2">
                <Globe size={18} className="text-slate-500" />
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yourbrand.com"
                  data-testid="brand-brain-website-input"
                  className="flex-1 bg-transparent text-slate-100 placeholder-slate-500 outline-none text-sm"
                />
              </div>
            </div>

            <button
              onClick={runUploadAnalysis}
              disabled={busy || (!logoFile && !websiteUrl)}
              data-testid="brand-brain-run-analysis"
              className="w-full rounded-lg bg-emerald-500 py-3 font-semibold text-white hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {busy ? 'Analyzing…' : (<>Analyze and continue <ArrowRight size={18} /></>)}
            </button>
          </section>
        )}

        {/* BUILD PATH */}
        {!loading && mode === 'build' && (
          <section
            data-testid="brand-brain-build-form"
            className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8 space-y-5"
          >
            <BackButton onClick={() => setMode(null)} />

            {/* Round 218A P0 (2026-02-07) — business_name / niche /
                audience come from Business Brain automatically.
                We never ask twice. This build form now collects ONLY
                the branding-specific pieces Business Brain doesn't
                know: vibe, colors, competitor references. */}

            <QField
              label="Vibe or mood the brand should convey"
              value={answers.vibe}
              onChange={(v) => setAnswers({ ...answers, vibe: v })}
              testid="brand-brain-q-vibe"
              placeholder="Warm and grounded, or bold and modern — describe the feeling"
            />
            <QField
              label="Colors you already love (or want to avoid)"
              value={answers.color_prefs}
              onChange={(v) => setAnswers({ ...answers, color_prefs: v })}
              testid="brand-brain-q-colors"
              placeholder="e.g., earthy neutrals, no neon"
            />
            <QField
              label="Competitor brands you admire (optional)"
              value={answers.competitors}
              onChange={(v) => setAnswers({ ...answers, competitors: v })}
              testid="brand-brain-q-competitors"
              placeholder="Names of brands whose look and feel you like"
            />

            <button
              onClick={runBuildGeneration}
              disabled={busy}
              data-testid="brand-brain-run-generate"
              className="w-full rounded-lg bg-emerald-500 py-3 font-semibold text-white hover:bg-emerald-400 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy ? 'Generating your brand kit…' : (<><Sparkle size={18} weight="fill" /> Generate my brand</>)}
            </button>
          </section>
        )}

        {/* REVIEW */}
        {!loading && mode === 'review' && brandBrain && (
          <BrandBrainReviewCard
            brand={brandBrain}
            onField={handleFieldChange}
            onBack={() => setMode(null)}
            onSave={saveEditsAndFinish}
            busy={busy}
            saveLabel="Save & generate my logos"
          />
        )}

        {/* LOGO STUDIO (Phase C) */}
        {!loading && mode === 'logos' && brandBrain && (
          <LogoStudio
            brand={brandBrain}
            onBrandUpdate={setBrandBrain}
            onBack={() => setMode('review')}
            onPrimarySelected={async (conceptId) => {
              setBusy(true);
              try {
                const r = await brandBrainAPI.selectPrimary(conceptId);
                setBrandBrain(r?.brand_brain || brandBrain);
                await brandBrainAPI.completeOnboarding();
                toast.success("Primary logo saved. Variants + Brand Guidelines PDF generated.");
                navigate('/brand-ready');
              } catch (err) {
                console.error(err);
                toast.error('Failed to finalize primary logo.');
              } finally {
                setBusy(false);
              }
            }}
            busy={busy}
            setBusy={setBusy}
          />
        )}
      </div>
    </div>
  );
};


// ─── Reusable pieces ─────────────────────────────────────────────
const PathCard = ({ icon: Icon, title, description, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="text-left rounded-2xl border border-slate-800 bg-slate-900/60 p-6 hover:border-emerald-400/70 hover:bg-slate-800/60 transition group"
  >
    <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-500/15 text-emerald-400 mb-4 group-hover:bg-emerald-500/25 transition">
      <Icon size={22} />
    </div>
    <div className="text-lg font-semibold text-white mb-1">{title}</div>
    <div className="text-sm text-slate-400 leading-relaxed">{description}</div>
  </button>
);

const BackButton = ({ onClick }) => (
  <button
    onClick={onClick}
    data-testid="brand-brain-back"
    className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200"
  >
    <ArrowLeft size={14} /> Back
  </button>
);

const QField = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-slate-200 mb-1">{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-emerald-400"
    />
  </div>
);


export const BrandBrainReviewCard = ({ brand, onField, onBack, onSave, busy, saveLabel }) => {
  const setListStr = (path, str) => {
    const arr = str.split(',').map((s) => s.trim()).filter(Boolean);
    onField(path, arr);
  };

  const setColorList = (path, str) => {
    const arr = str
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((hex) => ({ hex: hex.startsWith('#') ? hex.toUpperCase() : `#${hex.toUpperCase()}`, name: '' }));
    onField(path, arr);
  };

  const primaryStr = useMemo(
    () => (brand.primary_colors || []).map((c) => c.hex).join(', '),
    [brand.primary_colors],
  );
  const secondaryStr = useMemo(
    () => (brand.secondary_colors || []).map((c) => c.hex).join(', '),
    [brand.secondary_colors],
  );
  const personalityStr = useMemo(
    () => (brand.brand_personality || []).join(', '),
    [brand.brand_personality],
  );

  return (
    <section
      data-testid="brand-brain-review"
      className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8 space-y-6"
    >
      {onBack && <BackButton onClick={onBack} />}

      <div className="grid gap-6 sm:grid-cols-2">
        <ReviewField label="Business name" value={brand.business_name} onChange={(v) => onField('business_name', v)} testid="review-business-name" />
        <ReviewField label="Tagline" value={brand.tagline} onChange={(v) => onField('tagline', v)} testid="review-tagline" />

        <ReviewField label="Brand voice" value={brand.brand_voice} onChange={(v) => onField('brand_voice', v)} testid="review-brand-voice" />
        <ReviewField label="Brand style" value={brand.brand_style} onChange={(v) => onField('brand_style', v)} testid="review-brand-style" />

        <ReviewField label="Brand personality (comma-separated)" value={personalityStr} onChange={(v) => setListStr('brand_personality', v)} testid="review-brand-personality" />
        <ReviewField label="Website URL" value={brand.website_url} onChange={(v) => onField('website_url', v)} testid="review-website-url" />

        <ReviewField label="Heading font" value={brand.fonts?.heading} onChange={(v) => onField('fonts.heading', v)} testid="review-fonts-heading" />
        <ReviewField label="Body font" value={brand.fonts?.body} onChange={(v) => onField('fonts.body', v)} testid="review-fonts-body" />

        <ReviewField label="Icon style" value={brand.icon_style} onChange={(v) => onField('icon_style', v)} testid="review-icon-style" />
        <ReviewField label="Illustration style" value={brand.illustration_style} onChange={(v) => onField('illustration_style', v)} testid="review-illustration-style" />

        <ReviewField label="Photography style" value={brand.photography_style} onChange={(v) => onField('photography_style', v)} testid="review-photography-style" className="sm:col-span-2" />

        <ReviewField label="Primary colors (hex, comma-separated)" value={primaryStr} onChange={(v) => setColorList('primary_colors', v)} testid="review-primary-colors" />
        <ReviewField label="Secondary colors (hex, comma-separated)" value={secondaryStr} onChange={(v) => setColorList('secondary_colors', v)} testid="review-secondary-colors" />
      </div>

      {/* Color swatches */}
      {(brand.primary_colors?.length > 0 || brand.secondary_colors?.length > 0) && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Palette preview</div>
          <div className="flex flex-wrap gap-2">
            {[...(brand.primary_colors || []), ...(brand.secondary_colors || [])].map((c, idx) => (
              <div
                key={`${c.hex}-${idx}`}
                className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-1.5"
              >
                <span
                  className="inline-block h-5 w-5 rounded border border-slate-600"
                  style={{ background: c.hex }}
                />
                <span className="text-xs text-slate-300">{c.hex}</span>
                {c.name && <span className="text-xs text-slate-500">· {c.name}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {brand.logo_urls?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Uploaded logos</div>
          <div className="flex flex-wrap gap-3">
            {brand.logo_urls.map((logo, idx) => (
              <div key={idx} className="rounded-lg border border-slate-700 bg-slate-800/60 p-2">
                <div className="text-xs text-slate-400 mb-1">{logo.kind || 'logo'} · {logo.filename || ''}</div>
                <a href={logo.url} target="_blank" rel="noreferrer" className="text-xs text-emerald-400 underline">
                  {logo.storage_path}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {onSave && (
        <button
          onClick={onSave}
          disabled={busy}
          data-testid="brand-brain-save"
          className="w-full rounded-lg bg-emerald-500 py-3 font-semibold text-white hover:bg-emerald-400 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy ? 'Saving…' : (<><FloppyDisk size={18} /> {saveLabel || 'Save Brand Brain'}</>)}
        </button>
      )}
    </section>
  );
};

const ReviewField = ({ label, value, onChange, testid, className = '' }) => (
  <div className={className}>
    <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-emerald-400"
    />
  </div>
);

// ─────────────────────────────────────────────────────────────────
// Logo Studio (Phase C) — concept grid, generate/regenerate, mark
// favorite, delete, compare side-by-side, choose primary.
// ─────────────────────────────────────────────────────────────────
const LogoStudio = ({ brand, onBrandUpdate, onBack, onPrimarySelected, busy, setBusy }) => {
  const [generating, setGenerating] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [showComparator, setShowComparator] = useState(false);

  const concepts = (brand.logo_urls || []).filter((l) => l.kind === 'concept');

  const runGenerate = async (regen = false) => {
    setGenerating(true);
    try {
      const excludeDirections = regen
        ? Array.from(new Set(concepts.map((c) => c.direction_id).filter(Boolean)))
        : [];
      const count = regen ? 2 : 3;
      const r = await brandBrainAPI.generateLogos(count, excludeDirections);
      // Round 218E P0 (2026-02-07) — the API client (see
      // ``brandBrainAPI.generateLogos``) now polls a background job
      // and resolves ONLY on success with the refreshed brand_brain
      // doc. So we can trust ``r.brand_brain.logo_urls`` directly.
      const updatedBrain = r?.brand_brain || null;
      onBrandUpdate(updatedBrain || brand);
      const finalConcepts = (updatedBrain?.logo_urls || [])
        .filter((l) => l.kind === 'concept');
      if (finalConcepts.length) {
        toast.success(regen ? 'Two more concepts added.' : 'Concepts ready — pick your favorite.');
      } else {
        // Should never happen because the API client throws on
        // failure — but stay honest with the customer if it does.
        toast.error(
          'Generation returned no concepts. Please try again.',
        );
      }
    } catch (err) {
      console.error('[LogoStudio] generation failed', err);
      // Surface the real backend error instead of a generic string.
      const providerError =
        err?.response?.data?.detail
        || err?.message
        || 'Logo generation failed. Please try again.';
      toast.error(providerError);
    } finally {
      setGenerating(false);
    }
  };

  const removeConcept = async (conceptId) => {
    try {
      const r = await brandBrainAPI.deleteConcept(conceptId);
      onBrandUpdate(r?.brand_brain || brand);
      setSelectedForCompare((prev) => prev.filter((id) => id !== conceptId));
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete concept.');
    }
  };

  const toggleFavorite = async (entryId) => {
    try {
      const r = await brandBrainAPI.toggleFavorite(entryId);
      onBrandUpdate(r?.brand_brain || brand);
    } catch (err) {
      console.error(err);
    }
  };

  const toggleCompare = (conceptId) => {
    setSelectedForCompare((prev) => {
      if (prev.includes(conceptId)) return prev.filter((id) => id !== conceptId);
      if (prev.length >= 2) return [prev[1], conceptId]; // keep at most 2
      return [...prev, conceptId];
    });
  };

  return (
    <section
      data-testid="brand-brain-logo-studio"
      className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8 space-y-6"
    >
      <div className="flex items-center justify-between">
        <BackButton onClick={onBack} />
        {concepts.length > 0 && (
          <div className="text-xs text-slate-500">
            {concepts.length} concept{concepts.length === 1 ? '' : 's'} generated
          </div>
        )}
      </div>

      <div>
        <h2 className="text-2xl font-semibold text-white mb-2">Pick your primary logo</h2>
        <p className="text-slate-400 text-sm">
          Every builder will use your primary logo automatically. You can favorite, delete,
          or compare — then pick one to lock in as your brand identity.
        </p>
      </div>

      {concepts.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-700 bg-slate-800/40 p-8 text-center">
          <Sparkle size={28} weight="duotone" className="mx-auto text-emerald-400 mb-3" />
          <p className="text-slate-300 mb-4">
            Generate 3 distinct logo concepts based on your Brand Brain.
          </p>
          <button
            onClick={() => runGenerate(false)}
            disabled={generating}
            data-testid="logo-studio-generate"
            className="rounded-lg bg-emerald-500 px-6 py-3 font-semibold text-white hover:bg-emerald-400 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {generating ? 'Generating (this can take ~30 seconds)…' : (<><MagicWand size={18} /> Generate logo concepts</>)}
          </button>
        </div>
      )}

      {concepts.length > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {concepts.map((c) => (
              <ConceptCard
                key={c.id}
                concept={c}
                selectedForCompare={selectedForCompare.includes(c.concept_id)}
                onToggleCompare={() => toggleCompare(c.concept_id)}
                onToggleFavorite={() => toggleFavorite(c.id)}
                onDelete={() => removeConcept(c.concept_id)}
                onChoose={() => onPrimarySelected(c.concept_id)}
                busy={busy || generating}
              />
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-800">
            <button
              onClick={() => runGenerate(true)}
              disabled={generating}
              data-testid="logo-studio-regenerate"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/60 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-50"
            >
              <PlusCircle size={16} /> {generating ? 'Generating…' : 'Generate 2 more concepts'}
            </button>

            <button
              onClick={() => setShowComparator(true)}
              disabled={selectedForCompare.length !== 2}
              data-testid="logo-studio-compare"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/60 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-40"
            >
              <ArrowsHorizontal size={16} /> Compare side-by-side ({selectedForCompare.length}/2)
            </button>
          </div>
        </>
      )}

      {busy && (
        <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-4 text-sm text-slate-300 text-center">
          Locking in primary logo, generating variants, and creating your Brand Guidelines PDF…
          This can take up to a minute.
        </div>
      )}

      {showComparator && selectedForCompare.length === 2 && (
        <Comparator
          left={concepts.find((c) => c.concept_id === selectedForCompare[0])}
          right={concepts.find((c) => c.concept_id === selectedForCompare[1])}
          onClose={() => setShowComparator(false)}
          onChoose={(cid) => {
            setShowComparator(false);
            onPrimarySelected(cid);
          }}
        />
      )}
    </section>
  );
};

const ConceptCard = ({
  concept, selectedForCompare, onToggleCompare, onToggleFavorite,
  onDelete, onChoose, busy,
}) => (
  <div
    data-testid={`concept-card-${concept.concept_id}`}
    className={`rounded-xl border p-3 bg-slate-800/40 flex flex-col ${
      selectedForCompare ? 'border-emerald-400 ring-2 ring-emerald-500/30' : 'border-slate-700'
    }`}
  >
    <div className="relative rounded-lg bg-white/95 h-40 flex items-center justify-center overflow-hidden mb-3">
      <img
        src={concept.url}
        alt={concept.direction_label || 'Logo concept'}
        className="max-h-32 max-w-full object-contain"
      />
      {concept.is_favorite && (
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-yellow-400/95 text-slate-900 text-xs px-2 py-0.5 font-semibold">
          <Star size={12} weight="fill" /> Favorite
        </span>
      )}
    </div>

    <div className="flex-1 mb-3">
      <div className="text-xs uppercase tracking-widest text-emerald-400 mb-1">
        {concept.direction_label || 'Concept'}
      </div>
    </div>

    <div className="grid grid-cols-2 gap-2 mb-2">
      <button
        onClick={onToggleFavorite}
        disabled={busy}
        data-testid={`concept-favorite-${concept.concept_id}`}
        className={`inline-flex items-center justify-center gap-1 rounded-md py-1.5 text-xs border transition ${
          concept.is_favorite
            ? 'border-yellow-400/60 bg-yellow-400/10 text-yellow-300'
            : 'border-slate-700 bg-slate-800/60 text-slate-300 hover:bg-slate-800'
        }`}
      >
        <Star size={13} weight={concept.is_favorite ? 'fill' : 'regular'} />
        {concept.is_favorite ? 'Favorited' : 'Favorite'}
      </button>
      <button
        onClick={onDelete}
        disabled={busy}
        data-testid={`concept-delete-${concept.concept_id}`}
        className="inline-flex items-center justify-center gap-1 rounded-md py-1.5 text-xs border border-slate-700 bg-slate-800/60 text-slate-300 hover:bg-red-500/20 hover:text-red-300 hover:border-red-500/40"
      >
        <Trash size={13} /> Delete
      </button>
    </div>

    <div className="grid grid-cols-2 gap-2">
      <button
        onClick={onToggleCompare}
        disabled={busy}
        data-testid={`concept-compare-${concept.concept_id}`}
        className={`inline-flex items-center justify-center gap-1 rounded-md py-1.5 text-xs border transition ${
          selectedForCompare
            ? 'border-emerald-400/60 bg-emerald-500/15 text-emerald-300'
            : 'border-slate-700 bg-slate-800/60 text-slate-300 hover:bg-slate-800'
        }`}
      >
        <ArrowsHorizontal size={13} /> {selectedForCompare ? 'In compare' : 'Compare'}
      </button>
      <button
        onClick={onChoose}
        disabled={busy}
        data-testid={`concept-choose-${concept.concept_id}`}
        className="inline-flex items-center justify-center gap-1 rounded-md bg-emerald-500 text-white py-1.5 text-xs font-semibold hover:bg-emerald-400 disabled:opacity-50"
      >
        <CheckCircle size={13} weight="fill" /> Choose
      </button>
    </div>
  </div>
);

const Comparator = ({ left, right, onClose, onChoose }) => (
  <div
    className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    data-testid="logo-comparator"
  >
    <div className="w-full max-w-5xl bg-slate-900 border border-slate-700 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Side-by-side comparison</h3>
        <button
          onClick={onClose}
          data-testid="comparator-close"
          className="text-slate-400 hover:text-slate-200"
        >
          <X size={20} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {[left, right].map((concept, idx) => (
          <div key={concept.concept_id} className="rounded-lg border border-slate-700 bg-slate-800/40 p-4">
            <div className="text-xs uppercase tracking-widest text-emerald-400 mb-2">
              {concept.direction_label || 'Concept'}
            </div>
            <div className="rounded-lg bg-white/95 h-64 flex items-center justify-center overflow-hidden mb-3">
              <img src={concept.url} alt="" className="max-h-56 max-w-full object-contain" />
            </div>
            <button
              onClick={() => onChoose(concept.concept_id)}
              data-testid={`comparator-choose-${idx}`}
              className="w-full rounded-md bg-emerald-500 py-2 font-semibold text-white hover:bg-emerald-400 inline-flex items-center justify-center gap-2"
            >
              <CheckCircle size={16} weight="fill" /> Choose this
            </button>
          </div>
        ))}
      </div>
    </div>
  </div>
);


export default BrandBrainSetupPage;
